import { useCreateDecoratedExperienceEvents } from '@/features/events/hooks/useCreateDecoratedExperienceEvents';
import { useEventListFilters } from '@/features/events/hooks/useEventListFilters';
import { useGetPageExperienceEvents } from '@/features/events/hooks/useGetPageExperienceEvents';
import { useGetPublishedPageExperienceEvents } from '@/features/events/hooks/useGetPublishedPageExperienceEvents';
import type { DecoratedExperienceEvent } from '@/features/events/types';
import type { PageExperienceFragment } from '@/features/experiences/fragments/PageExperience.generated';
import { getDisplayTimezone } from '@/features/experiences/lib/experience';
import type { ListExperienceEventInput } from '@/types/gql.generated';
import { getLocalTimeZone } from '@/utils/dates';
import type { QueryError } from '@/utils/errors';

type Arg = {
  experience: PageExperienceFragment | undefined;
  filters?: ListExperienceEventInput;
  published?: boolean;
  /** False skips the request entirely; `events` is then empty and not loading. */
  enabled?: boolean;
};

type Ret = {
  isLoading: boolean;
  error: QueryError | null;
  events: DecoratedExperienceEvent[];
};

export const useExperienceEvents = ({
  experience,
  filters: providedFilters = {},
  published = false,
  enabled = true,
}: Arg): Ret => {
  const defaultFilters = useEventListFilters({ experience });
  const filters = { ...defaultFilters, ...providedFilters };

  // Both hooks treat an undefined key as "do not fetch".
  const liveQuery = useGetPageExperienceEvents(
    published || !enabled ? undefined : experience?.id,
    filters
  );
  const publishedQuery = useGetPublishedPageExperienceEvents(
    published && enabled ? (experience?.publishedSlug ?? undefined) : undefined,
    filters
  );
  const {
    data: eventsData = [],
    isPending,
    error,
  } = published ? publishedQuery : liveQuery;
  // A disabled query reports pending forever; it is not loading anything.
  const isLoading = enabled && isPending;

  const displayTimezone = experience
    ? getDisplayTimezone(experience)
    : getLocalTimeZone();

  const { decoratedExperienceEvents } = useCreateDecoratedExperienceEvents(
    experience,
    eventsData,
    displayTimezone
  );

  return {
    isLoading,
    error,
    events: decoratedExperienceEvents,
  };
};
